import {
  ConsoleTransport,
  ErrorsInstrumentation,
  FetchTransport,
  getWebInstrumentations,
  initializeFaro,
  type Faro,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

let faroInstance: Faro | null = null;
let fullRumEnabled = false;
const fullRumListeners = new Set<() => void>();

const notifyFullRumListeners = (): void => {
  fullRumListeners.forEach((listener) => listener());
};

/**
 * Builds the transport list based on environment.
 *
 * - In production/staging: FetchTransport sends to Grafana Cloud.
 * - In development: FetchTransport is skipped (prod collector blocks CORS
 *   from localhost). Set NEXT_PUBLIC_FARO_CONSOLE_TRANSPORT=true to log
 *   signals to the browser console instead.
 */
function buildTransports(faroUrl: string) {
  return [
    ...(process.env.NODE_ENV !== 'development' ? [new FetchTransport({ url: faroUrl })] : []),
    ...(process.env.NEXT_PUBLIC_FARO_CONSOLE_TRANSPORT === 'true' ? [new ConsoleTransport()] : []),
  ];
}

const tracingInstrumentation = () =>
  new TracingInstrumentation({
    instrumentationOptions: {
      propagateTraceHeaderCorsUrls: [
        /^https?:\/\/localhost(:\d+)?\//,
        /^https:\/\/[^/]*\.venienergy\.com\//,
        /^https:\/\/[^/]*\.eneas\.no\//,
        /^https:\/\/[^/]*\.eneas\.se\//,
        /^https:\/\/[^/]*\.venienergia\.fi\//,
        /^https:\/\/[^/]*\.nordelenergi\.se\//,
        /^https:\/\/[^/]*\.gu-veni\.de\//,
      ],
    },
  });

const getFullRumInstrumentations = () => [
  ...getWebInstrumentations({ captureConsole: true, enablePerformanceInstrumentation: true }),
  tracingInstrumentation(),
];

/**
 * Initialises Grafana Faro browser SDK (once).
 *
 * `faroUrl` and `appName` are passed explicitly from the server-side layout so
 * that they are read at runtime (from container env / Key Vault) rather than
 * being inlined into the client bundle at build time. This avoids the need for
 * the `NEXT_PUBLIC_` prefix and allows a single Docker image to run in all
 * environments.
 *
 * Starts with operational error monitoring only. Full RUM is added explicitly
 * after Analytics consent so the initial browser state has no persistent Faro
 * session, page views, user actions, performance data, console capture, or
 * distributed tracing.
 *
 * Safe to call multiple times — returns the existing instance after the first
 * call. Must only run in a browser context (guards against SSR execution).
 */
export function initFaro(
  faroUrl?: string,
  appName: string = 'Veni.Sourcing.Portal.UI',
  runtimeEnv?: string,
): Faro | null {
  // SSR guard — never run on the server
  if (typeof window === 'undefined') return null;

  // Return existing instance on subsequent calls
  if (faroInstance) return faroInstance;

  if (!faroUrl) {
    console.warn('[Faro] FARO_URL is not set — Faro will not be initialised.');
    return null;
  }

  const transports = buildTransports(faroUrl);
  if (transports.length === 0) {
    // No transports configured (e.g. development without NEXT_PUBLIC_FARO_CONSOLE_TRANSPORT).
    // Skip init entirely — Faro would fall back to a default FetchTransport otherwise.
    return null;
  }

  faroInstance = initializeFaro({
    app: {
      name: appName,
      version: process.env.NEXT_PUBLIC_APP_VERSION ?? '1.0.0',
      // Prefer the runtime value passed from PublicEnvVarsProvider so the
      // environment tag is correct in a single multi-env Docker image; fall
      // back to build-time env only when not provided.
      environment:
        runtimeEnv ?? process.env.NEXT_PUBLIC_RUNTIME_ENV ?? process.env.NODE_ENV ?? 'production',
    },
    transports,
    sessionTracking: {
      // The session instrumentation is added only after Analytics consent.
      // Once added, this preserves the current localStorage behavior.
      persistent: true,
    },
    instrumentations: [new ErrorsInstrumentation()],
  });

  return faroInstance;
}

/**
 * Enables the full RUM instrumentation after explicit Analytics consent.
 * The persistent session behavior intentionally remains unchanged for now.
 */
export const enableFullRum = (): void => {
  if (!faroInstance || fullRumEnabled) return;

  faroInstance.instrumentations.add(...getFullRumInstrumentations());
  fullRumEnabled = true;
  notifyFullRumListeners();
};

export const disableFullRum = (): void => {
  if (!faroInstance) return;

  if (fullRumEnabled) {
    faroInstance.pause();
  }

  faroInstance.api.resetUser();
  faroInstance.api.resetSession();
  window.localStorage.removeItem('com.grafana.faro.session');

  if (!fullRumEnabled) return;

  fullRumEnabled = false;
  notifyFullRumListeners();
  window.location.reload();
};

export const isFullRumEnabled = (): boolean => fullRumEnabled;

export const subscribeToFullRum = (listener: () => void): (() => void) => {
  fullRumListeners.add(listener);
  return () => fullRumListeners.delete(listener);
};

/**
 * Returns the current Faro instance (or null if not yet initialised).
 * Use this in utility code that should not force an init.
 */
export function getFaro(): Faro | null {
  return faroInstance;
}
